import React, { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { CustomCircularProgress } from 'Components/StyledComponents';
import PaymentFlow from './Payment/PaymentFlow';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import PendingDeletionModal from './PendingDeletionModal';
import { hasAuthParams } from 'react-oidc-context';
type ProtectedLayoutProps = { children: React.ReactNode };
const ProtectedLayout: React.FC<ProtectedLayoutProps> = ({ children }) => (
  <>
    <PaymentFlow />
    {children}
    <PendingDeletionModal />
  </>
);

type ProtectedRouteProps = {
  redirectPath: string;
} & React.PropsWithChildren;

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, redirectPath }) => {
  const { isAuthenticated, isLoading, oauthPending } = useAuthWrapperContext();
  const navigate = useNavigate();

  // Check if we're in the middle of an OAuth LOGIN callback (has auth params in URL)
  const isOAuthLoginCallback = hasAuthParams();

  useEffect(() => {
    if (
      location.pathname == '/' &&
      !isLoading &&
      !oauthPending &&
      !isOAuthLoginCallback &&
      !isAuthenticated
    ) {
      navigate('/signin');
    } else if (location.pathname == '/' && !isLoading && !oauthPending && isAuthenticated) {
      navigate('/roadmap');
    }
  }, [isAuthenticated, isOAuthLoginCallback, location.pathname, navigate]);

  // Show loading during OAuth login callback processing
  if ((isLoading || oauthPending || isOAuthLoginCallback) && !isAuthenticated) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CustomCircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated && !isLoading && !oauthPending && !isOAuthLoginCallback) {
    return <Navigate to={redirectPath} replace />;
  }

  return <ProtectedLayout>{children}</ProtectedLayout>;
};

export default ProtectedRoute;
